/* 像素办公室 · 场景生成器（纯数据，无 React / 无 DOM）
 * buildLobbyScene(projects)                                       —— 大厅：按真实项目动态生成工位岛 + 标配家具
 * buildProjectRoomScene(projectId, projectName)                   —— 项目内部：全功能入口房间（旧版，保留备用）
 * buildProjectFloorScene(projectId, projectName, episodes)        —— 项目楼层：集门厅（分集门）+ 项目级功能房
 * buildEpisodeRoomScene(projectId, episodeId, projectName, episodeLabel) —— 集功能大厅：本集全部功能入口
 *
 * 瓦片字符约定见 engine.js 头部注释。
 */

// ---- 大厅布局常量 ----
const ISLAND_W = 5;      // 工位岛宽（5 列地毯）
const ISLAND_H = 5;      // 工位岛高（4 行地毯 + 南沿地垫行）
const GAP_X = 3;         // 岛间横向间距（格）
const GAP_Y = 3;         // 岛间纵向间距（格）
const PER_ROW = 3;       // 每行 3 岛
const ISLAND_X0 = 8;     // 首岛左上角 x（左侧留出前台/公告栏通道）
const HEADER_H = 6;      // 顶部：墙 + 公告栏/荣誉墙 + 前台区
const FOOTER_H = 8;      // 底部：沙发 + 咖啡角 + 盆栽 + 墙

const CARPET_KEYS = ['a', 'b', 'c', 'd', 'e']; // 5 色地毯循环

// Presentation-only metadata: the collision grid and every existing route stay unchanged.
function roomKind(name) {
  if (/音乐|音频/.test(name)) return 'music';
  if (/资源|画廊/.test(name)) return 'gallery';
  if (/角色|场景|道具|资产/.test(name)) return 'assets';
  if (/费用/.test(name)) return 'cost';
  if (/大纲/.test(name)) return 'outline';
  if (/剧本|改编|教学/.test(name)) return 'script';
  if (/故事板|分镜|主镜|粗剪|画布|视角/.test(name)) return 'storyboard';
  return 'production';
}

/** 桌牌项目名：超过 8 字截断 */
export function truncateName(name, max = 8) {
  const text = String(name || '未命名项目');
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** 小镇只呈现项目已有字段，不推断生成任务是否完成。 */
export function projectTownStatus(project) {
  const delivered = project?.projectStatus === 'delivered';
  const deadline = project?.deadline ? new Date(`${String(project.deadline).slice(0, 10)}T23:59:59`) : null;
  const overdue = !delivered && deadline && Number.isFinite(deadline.getTime()) && deadline.getTime() < Date.now();
  if (delivered) return { key: 'delivered', label: '已交付', activity: '整理交付档案' };
  if (overdue) return { key: 'overdue', label: '已延期', activity: '核对延期事项' };
  return { key: 'production', label: '制作中', activity: '推进今日制作' };
}

/**
 * 大厅地图动态生成算法：
 * 1. 项目按 index 排序，每行 3 岛，岛距 3 格，地图宽度按每行岛数固定推导：
 *    width = ISLAND_X0 + PER_ROW*5 + (PER_ROW-1)*3 + 右边距 3 = 32
 * 2. 高度随岛行数增长：HEADER_H + 岛区(5N + 3(N-1) + 3) + FOOTER_H
 * 3. 每岛 5×4 地毯（颜色 5 色循环）+ 南沿地垫（中心格），桌牌悬浮在岛上方；
 *    地毯第 3 行嵌入两个工位桌（T），保持与原型一致的节奏。
 * 4. 标配家具固定：前台(R, 2×2)、公告栏(B)×2、荣誉墙(H)、咖啡机(M)×2、沙发(S, 2×2)、盆栽(P)×4。
 * 5. 前台右侧一块"更衣室"地垫（custom 动作，打开换装弹窗）。
 */
export function buildLobbyScene(projects) {
  const list = Array.isArray(projects) ? projects : [];
  const islandRows = Math.ceil(list.length / PER_ROW);
  const width = ISLAND_X0 + PER_ROW * ISLAND_W + (PER_ROW - 1) * GAP_X + 3;
  const islandsH = islandRows > 0
    ? islandRows * ISLAND_H + (islandRows - 1) * GAP_Y + GAP_Y
    : 2;
  const footerY = HEADER_H + islandsH;
  const height = footerY + FOOTER_H;

  // 底图：全地板，四周围墙
  const rows = Array.from({ length: height }, (_, ty) => (
    Array.from({ length: width }, (_, tx) => (
      ty === 0 || ty === height - 1 || tx === 0 || tx === width - 1 ? '#' : '.'
    ))
  ));
  const put = (tx, ty, ch) => { rows[ty][tx] = ch; };

  // ---- 标配：公告栏 / 荣誉墙（贴在顶墙下沿） ----
  put(5, 1, 'B');
  put(Math.floor(width / 2), 1, 'H');
  put(width - 8, 1, 'B');

  // ---- 标配：前台 2×2 ----
  put(3, 3, 'R'); put(4, 3, 'R'); put(3, 4, 'R'); put(4, 4, 'R');

  // ---- 工位岛 ----
  const mats = [];
  const npcs = [];
  const zones = [];
  const labels = [
    { x: 3.5, y: 2.4, text: '前台', small: true },
    { x: 5, y: 0.55, text: '公告栏', small: true },
    { x: Math.floor(width / 2), y: 0.55, text: '荣誉墙', small: true },
  ];
  list.forEach((project, i) => {
    const row = Math.floor(i / PER_ROW);
    const col = i % PER_ROW;
    const ox = ISLAND_X0 + col * (ISLAND_W + GAP_X);
    const oy = HEADER_H + row * (ISLAND_H + GAP_Y);
    const carpet = CARPET_KEYS[i % CARPET_KEYS.length];
    for (let dy = 0; dy < ISLAND_H; dy++) {
      for (let dx = 0; dx < ISLAND_W; dx++) {
        put(ox + dx, oy + dy, carpet);
      }
    }
    // 第 3 行嵌入两个工位桌
    put(ox + 1, oy + 2, 'T');
    put(ox + 3, oy + 2, 'T');
    // 南沿中心地垫
    put(ox + 2, oy + ISLAND_H - 1, '1');
    const short = truncateName(project?.name || project?.title);
    const fullName = String(project?.name || project?.title || '未命名项目');
    const status = projectTownStatus(project);
    zones.push({
      x: ox, y: oy, w: ISLAND_W, h: ISLAND_H, kind: 'production',
      title: fullName, subtitle: status.label, projectId: String(project.id),
      coverUrl: typeof project.coverImage === 'string' ? project.coverImage : '',
    });
    mats.push({
      tx: ox + 2,
      ty: oy + ISLAND_H - 1,
      label: `进入《${short}》工位岛`,
      fullLabel: fullName,
      description: `项目 #${project.id} · ${status.label}`,
      projectId: String(project.id),
      coverUrl: typeof project.coverImage === 'string' ? project.coverImage : '',
      action: {
        type: 'route',
        path: `/project/${project.id}/office`,
        state: { projectName: project?.name || project?.title || '未命名项目' },
      },
    });
    // 桌牌：岛上方居中
    labels.push({ x: ox + 2, y: oy - 0.9, text: `《${short}》`, zoneLabel: true });
    labels.push({ x: ox + 2, y: oy + 0.2, text: status.label, small: true, zoneLabel: true });
    npcs.push({
      name: `${truncateName(short, 5)} · 制片`,
      x: ox + 0.7,
      y: oy + 3.55,
      activity: status.activity,
      wanderRadius: 1.25,
      paletteIndex: i,
      lines: status.key === 'overdue'
        ? ['这部项目已经过了计划日期，今天先把最关键的一步收住。', '不用全线赶工，先确认下一项能交付的内容。']
        : status.key === 'delivered'
          ? ['这一部已经交付，资料还在工位岛里，随时可以回来查看。', '交付不是消失，历史内容都还留在项目里。']
          : ['项目正在制作中，进工位岛就能看到分集和各个功能房。', '我先核对今天的进度，你可以直接从门垫进入。'],
    });
  });

  // ---- 更衣室地垫（前台右侧） ----
  put(6, 4, '1');
  mats.push({
    tx: 6, ty: 4,
    label: '进入 更衣室 · 换装',
    action: { type: 'custom', id: 'dressing' },
  });
  labels.push({ x: 6, y: 3.1, text: '更衣室', small: true });

  // ---- 标配：底部沙发 / 咖啡角 / 盆栽 ----
  put(5, footerY, 'P');
  put(width - 7, footerY, 'P');
  put(3, footerY + 2, 'S'); put(4, footerY + 2, 'S');
  put(3, footerY + 3, 'S'); put(4, footerY + 3, 'S');
  put(width - 6, footerY + 2, 'M');
  put(width - 5, footerY + 2, 'M');
  put(5, footerY + 5, 'P');
  put(width - 7, footerY + 5, 'P');
  labels.push({ x: 4, y: footerY + 1.4, text: '休息区', small: true });
  labels.push({ x: width - 6, y: footerY + 1.4, text: '咖啡角', small: true });

  npcs.push({
    name: '小瓜 · 前台',
    x: 6.5,
    y: footerY + 2.5,
    activity: '整理今日通告',
    wanderRadius: 1.4,
    paletteIndex: list.length + 1,
    lines: list.length > 0
      ? [`今天小镇里有 ${list.length} 个项目，公告栏会按真实状态排出优先事项。`, '点击地面也能移动，靠近门垫后点击提示就能进入。']
      : ['小镇还没有项目，切回列表模式就可以创建第一部作品。', '换好形象后，我会一直在前台等你回来。'],
  });

  return {
    id: 'lobby',
    grid: rows.map((r) => r.join('')),
    labels,
    mats,
    zones,
    spawn: { x: 5.5, y: 5.5 },
    crumb: '漫剧小镇 · 大厅',
    npcs,
    hotspots: [
      {
        tx: 5,
        ty: 1,
        range: 1.65,
        label: '查看 今日片场',
        action: { type: 'custom', id: 'town-board' },
      },
      {
        tx: Math.floor(width / 2),
        ty: 1,
        range: 1.65,
        label: '查看 项目进度',
        action: { type: 'custom', id: 'town-board' },
      },
    ],
    coffee: { tx: width - 6, ty: footerY + 2 },
    ambience: 'local',
  };
}

/**
 * 项目内部 · 全功能入口房间（固定 26×11 地图，地垫动作按 projectId 生成真实路由）
 */
export function buildProjectRoomScene(projectId, projectName) {
  const grid = [
    '##########################',
    '#..aa...bb...cc...dd.....#',
    '#.Taa..Tbb..Tcc..Tdd..P..#',
    '#..aa...bb...cc...dd.....#',
    '#..11...22...33...44.....#',
    '#........................#',
    '#..P..................P..#',
    '#..ee...ff...gg..........#',
    '#..ee.K.ff...gg......P...#',
    '#..55...66...77........q.#',
    '##########################',
  ];
  const short = truncateName(projectName);
  const route = (suffix) => ({ type: 'route', path: `/project/${projectId}${suffix}` });
  const pair = (tx, ty, label, action) => ([
    { tx, ty, label, action },
    { tx: tx + 1, ty, label, action },
  ]);

  return {
    id: `project-${projectId}:legacy-room`,
    grid,
    labels: [
      { x: 3.5, y: 0.55, text: '分镜室', small: true },
      { x: 8.5, y: 0.55, text: '剪辑室', small: true },
      { x: 13.5, y: 0.55, text: '资产间', small: true },
      { x: 18.5, y: 0.55, text: '改编坊', small: true },
      { x: 3.5, y: 6.55, text: '音乐角', small: true },
      { x: 8.5, y: 6.55, text: '大纲阁', small: true },
      { x: 13.5, y: 6.55, text: '费用房', small: true },
      { x: 23, y: 8.55, text: '出口', small: true },
    ],
    mats: [
      ...pair(3, 4, '进入 分镜室 · 选集做分镜', route('')),
      ...pair(8, 4, '进入 剪辑室 · 进集后选粗剪台', route('')),
      ...pair(13, 4, '进入 资产间 · 角色/场景/道具', route('/assets/characters')),
      ...pair(18, 4, '进入 改编坊 · AI 改编', route('/adaptation-agent')),
      ...pair(3, 9, '进入 音乐角 · 配乐生成', route('/music')),
      ...pair(8, 9, '进入 大纲阁 · 项目大纲', route('/outline')),
      ...pair(13, 9, '进入 费用房 · 成本统计', route('/cost')),
      { tx: 23, ty: 9, label: '返回大厅', action: { type: 'route', path: '/' } },
    ],
    spawn: { x: 12.5, y: 5.5 },
    crumb: `《${short}》 · 功能入口`,
    npcs: [],
    coffee: null,
    ambience: 'local',
  };
}

// ============================ 项目楼层 / 集功能大厅 布局常量 ============================
const DOOR_W = 2;          // 集门宽（2 格门垫）
const DOOR_GAP = 3;        // 集门横向间距（格）
const DOORS_PER_ROW = 4;   // 集门厅每行 4 门
const ALCOVE_W = 2;        // 功能房 / 壁龛宽（格）
const ALCOVE_GAP = 2;      // 功能房横向间距（格）
const HALL_X0 = 3;         // 首门 / 首房左上角 x
const FLOOR_WIDTH = 27;    // 楼层固定宽：4 扇集门与 6 间项目功能房共用
const MAT_GLYPHS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']; // 门垫字符循环（'q' 固定留给出口）

/** 集排序号：sortOrder 优先，episodeNumber 兜底，再退化到 fallback */
export function episodeOrderOf(ep, fallback = 0) {
  const n = Number(ep?.sortOrder || ep?.episodeNumber || 0);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * 集显示名："第 N 集 标题"（剥掉标题里重复的"第 N 集 / EP N"前缀）。
 * 与 dashboard/EpisodeSelector 的 episodeDisplayTitle 口径一致。
 */
export function episodeDisplayName(ep, fallback = 0) {
  const order = episodeOrderOf(ep, fallback);
  const clean = String(ep?.title || ep?.name || '')
    .trim()
    .replace(/^\s*#{1,6}\s*/, '')
    .replace(/^第\s*[一二三四五六七八九十百千万零〇两\d]+\s*[集章幕回话节部]\s*[：:、.．\-—_\s]*/, '')
    .replace(/^(?:episode|ep|e)\.?\s*0*\d+\s*[：:、.．\-—_\s]*/i, '')
    .trim();
  if (order) return clean ? `第${order}集 ${clean}` : `第${order}集`;
  return clean || '未命名集数';
}

/**
 * 项目楼层：上方"集门厅"——每集一扇门（门楣地毯 + 双格门垫 + 上方"第 N 集"名牌，
 * 集按集数排序，每行 4 门自动网格排布）；下方 6 间项目级功能房
 * （资产间/资源画廊/改编坊/音乐角/大纲阁/费用房）；底部出口地垫返回大厅。
 * 0 集时集门厅只留一块空态名牌（"该项目还没有集"），功能房与出口照常渲染。
 */
export function buildProjectFloorScene(projectId, projectName, episodes) {
  const width = FLOOR_WIDTH;
  const list = (Array.isArray(episodes) ? [...episodes] : []).sort((l, r) => (
    (episodeOrderOf(l) - episodeOrderOf(r)) || (Number(l?.id || 0) - Number(r?.id || 0))
  ));

  const doorRows = Math.ceil(list.length / DOORS_PER_ROW);
  const roomsY = list.length > 0 ? 4 * doorRows + 4 : 8; // 功能房地毯顶行（0 集时留 3 行空态区）
  const exitY = roomsY + 5;                              // 出口/休息区行（上方留 1 行走廊）
  const height = exitY + 2;

  const rows = Array.from({ length: height }, (_, ty) => (
    Array.from({ length: width }, (_, tx) => (
      ty === 0 || ty === height - 1 || tx === 0 || tx === width - 1 ? '#' : '.'
    ))
  ));
  const put = (tx, ty, ch) => { rows[ty][tx] = ch; };
  const mats = [];
  const labels = [];
  const zones = [];

  // ---- 顶部装饰：公告栏 / 荣誉墙 + 区域牌 ----
  put(4, 1, 'B'); put(11, 1, 'H'); put(18, 1, 'B');
  labels.push({ x: 1.2, y: 1.6, text: '集门厅', small: true });

  // ---- 集门厅：一集一扇门 ----
  list.forEach((ep, i) => {
    const r = Math.floor(i / DOORS_PER_ROW);
    const c = i % DOORS_PER_ROW;
    const ox = HALL_X0 + c * (DOOR_W + DOOR_GAP);
    const lintelY = 3 + r * 4;  // 门楣地毯行（每门节奏：门楣 1 + 门垫 1 + 走廊 2）
    const matY = lintelY + 1;
    const carpet = CARPET_KEYS[i % CARPET_KEYS.length];
    const glyph = MAT_GLYPHS[i % MAT_GLYPHS.length];
    put(ox, lintelY, carpet); put(ox + 1, lintelY, carpet);
    put(ox, matY, glyph); put(ox + 1, matY, glyph);
    const name = episodeDisplayName(ep, i + 1);
    const action = {
      type: 'route',
      path: `/project/${projectId}/episode/${ep.id}/office`,
      state: { projectName: projectName || '项目空间', episodeLabel: name },
    };
    const hasScript = Boolean(String(ep?.scriptContent || '').trim());
    const metadata = {
      fullLabel: name, description: hasScript ? '已有剧本 · 分集工作间' : '待补剧本 · 分集工作间',
      projectId: String(projectId),
    };
    mats.push({ tx: ox, ty: matY, label: `进入 ${name}`, action, ...metadata });
    mats.push({ tx: ox + 1, ty: matY, label: `进入 ${name}`, action, ...metadata });
    zones.push({
      x: ox, y: lintelY, w: 2, h: 2, kind: 'episode', title: name,
      subtitle: hasScript ? '已有剧本' : '待补剧本', projectId: String(projectId),
    });
    labels.push({
      x: ox + 0.5,
      y: lintelY - 0.55,
      text: `${hasScript ? '●' : '○'} ${truncateName(name, 8)}`,
      small: true,
      zoneLabel: true,
    });
  });

  if (list.length === 0) {
    labels.push({ x: Math.floor(width / 2) - 0.5, y: 3.6, text: '该项目还没有集' });
  }

  // ---- 项目级功能房 6 间（3 行配色地毯 + 房内工位桌 + 底部双格门垫） ----
  const ROOMS = [
    { name: '资产间', suffix: '/assets/characters' },
    { name: '资源画廊', actionId: 'resource-gallery' },
    { name: '改编坊', suffix: '/adaptation-agent' },
    { name: '音乐角', suffix: '/music' },
    { name: '大纲阁', suffix: '/outline' },
    { name: '费用房', suffix: '/cost' },
  ];
  ROOMS.forEach((room, i) => {
    const ox = HALL_X0 + i * (ALCOVE_W + ALCOVE_GAP);
    const carpet = CARPET_KEYS[i % CARPET_KEYS.length];
    const glyph = MAT_GLYPHS[(i + 4) % MAT_GLYPHS.length];
    for (let dy = 0; dy < 3; dy += 1) {
      put(ox, roomsY + dy, carpet); put(ox + 1, roomsY + dy, carpet);
    }
    put(ox, roomsY + 1, 'T'); // 房内一张工位桌（屏幕荧光点缀）
    put(ox, roomsY + 3, glyph); put(ox + 1, roomsY + 3, glyph);
    const action = room.actionId
      ? { type: 'custom', id: room.actionId }
      : { type: 'route', path: `/project/${projectId}${room.suffix}` };
    const metadata = { fullLabel: room.name, description: '项目公共工位', projectId: String(projectId) };
    mats.push({ tx: ox, ty: roomsY + 3, label: `进入 ${room.name}`, action, ...metadata });
    mats.push({ tx: ox + 1, ty: roomsY + 3, label: `进入 ${room.name}`, action, ...metadata });
    zones.push({ x: ox, y: roomsY, w: 2, h: 4, kind: roomKind(room.name), title: room.name });
    labels.push({ x: ox + 0.5, y: roomsY - 0.55, text: room.name, small: true, zoneLabel: true });
  });

  // ---- 底部：休息区 / 盆栽 / 咖啡机 + 出口 ----
  put(3, exitY, 'S'); put(4, exitY, 'S');
  put(6, exitY, 'P'); put(width - 8, exitY, 'P');
  put(width - 2, exitY, 'M');
  put(width - 4, exitY, 'q');
  mats.push({ tx: width - 4, ty: exitY, label: '返回大厅', action: { type: 'route', path: '/' } });
  labels.push({ x: 3.5, y: exitY - 0.45, text: '休息区', small: true });
  labels.push({ x: width - 4, y: exitY - 0.45, text: '出口', small: true });

  const short = truncateName(projectName);
  const scriptedCount = list.filter((ep) => String(ep?.scriptContent || '').trim()).length;
  return {
    id: `project-${projectId}:floor`,
    grid: rows.map((r) => r.join('')),
    labels,
    mats,
    zones,
    spawn: { x: Math.floor(width / 2) + 0.5, y: roomsY + 4.5 },
    crumb: `《${short}》 · 选集楼层`,
    npcs: [
      {
        name: '阿策 · 统筹',
        x: 2.2,
        y: roomsY + 4.55,
        activity: '核对分集',
        wanderRadius: 1.1,
        lines: list.length > 0
          ? [`当前共 ${list.length} 集，其中 ${scriptedCount} 集已有剧本。`, '门牌前的实心点表示已有剧本，空心点表示暂未检测到剧本文本。']
          : ['这里还没有分集，项目功能房仍然可以使用。'],
      },
      {
        name: '阿制 · 制片',
        x: width - 3.4,
        y: roomsY + 4.45,
        activity: '巡视功能房',
        wanderRadius: 1.15,
        paletteIndex: 2,
        lines: ['上方进入具体分集，下方可以逛资源画廊，也能处理资产、改编、音乐和费用。', '每个楼层会记住你上次停下的位置。'],
      },
    ],
    coffee: { tx: width - 2, ty: exitY },
    ambience: 'local',
  };
}

// ============================ 集功能大厅 ============================

/** 集功能房分组：组内一行壁龛（2 格门垫 + 上方名牌 + 分组配色地毯），组间留走廊。
 *  tab 即单集工作台 :tab 参数；rough-cut 走专用路由（见下方 path 生成）。 */
const EPISODE_ROOM_GROUPS = [
  {
    title: '故事制作', carpet: 'a',
    rooms: [
      { name: '本集剧本', tab: 'upload' },
      { name: '小故事板', tab: 'mini-storyboard' },
      { name: '故事板导演台', tab: 'storyboard-board' },
      { name: '分镜编辑器', tab: 'storyboard' },
      { name: '主镜参考', tab: 'master-shot' },
    ],
  },
  {
    title: '画布与后期', carpet: 'b',
    rooms: [
      { name: '画布模式', tab: 'canvas' },
      { name: '动态分镜', tab: 'motion-storyboard' },
      { name: '粗剪台', tab: 'rough-cut' },
      { name: '3D 视角', tab: 'orbit' },
    ],
  },
  {
    title: '生成工具', carpet: 'c',
    rooms: [
      { name: '图片生成', tab: 'imageagent' },
      { name: '视频生成', tab: 'videoagent' },
      { name: '音乐音频', tab: 'musicagent' },
      { name: '资源中心', tab: 'resources' },
    ],
  },
  {
    title: '资产与教学', carpet: 'd',
    rooms: [
      { name: '角色工坊', tab: 'character' },
      { name: '场景工坊', tab: 'scene' },
      { name: '道具工坊', tab: 'prop' },
      { name: '智能改编', tab: 'ai-writer' },
      { name: '教学中心', tab: 'education' },
    ],
  },
];

/**
 * 集功能大厅：四组功能房按组分行排布，走廊放盆栽点缀，底部出口返回项目楼层。
 * 地图宽按"每组最多房数"推导，高按组数推导；粗剪台使用专用路由，其余走 :tab 通配。
 */
export function buildEpisodeRoomScene(projectId, episodeId, projectName, episodeLabel) {
  const perRow = Math.max(...EPISODE_ROOM_GROUPS.map((g) => g.rooms.length));
  const width = HALL_X0 + perRow * ALCOVE_W + (perRow - 1) * ALCOVE_GAP + 2;
  const TOP_Y = 3;    // 第一组走廊行
  const BLOCK_H = 6;  // 每组节奏：走廊 1 + 地毯 3 + 门垫 1 + 组间隔 1
  const lastGroupY = TOP_Y + (EPISODE_ROOM_GROUPS.length - 1) * BLOCK_H;
  const exitY = lastGroupY + 6; // 末组门垫行 + 1 行走廊
  const height = exitY + 2;

  const rows = Array.from({ length: height }, (_, ty) => (
    Array.from({ length: width }, (_, tx) => (
      ty === 0 || ty === height - 1 || tx === 0 || tx === width - 1 ? '#' : '.'
    ))
  ));
  const put = (tx, ty, ch) => { rows[ty][tx] = ch; };
  const mats = [];
  const labels = [];
  const zones = [];

  // ---- 顶部装饰：公告栏 / 荣誉墙 / 盆栽 + 区域牌 ----
  put(4, 1, 'B'); put(11, 1, 'H'); put(18, 1, 'B');
  put(1, 2, 'P'); put(width - 2, 2, 'P');
  labels.push({ x: 2.2, y: 1.6, text: '功能大厅', small: true });

  // ---- 四组功能房 ----
  EPISODE_ROOM_GROUPS.forEach((group, g) => {
    const gy = TOP_Y + g * BLOCK_H;
    labels.push({ x: 1.2, y: gy + 0.15, text: group.title, small: true }); // 组名牌：走廊左端
    put(width - 2, gy, 'P');                                                // 走廊右端盆栽
    group.rooms.forEach((room, i) => {
      const ox = HALL_X0 + i * (ALCOVE_W + ALCOVE_GAP);
      for (let dy = 1; dy <= 3; dy += 1) {
        put(ox, gy + dy, group.carpet); put(ox + 1, gy + dy, group.carpet);
      }
      // Real equipment occupies the same solid tile in both views. The two-tile
      // entrance and front approach stay clear, so render depth never invents walls.
      put(ox, gy + 2, roomKind(room.name) === 'music' ? 'K' : 'T');
      const glyph = MAT_GLYPHS[(g * 2 + i) % MAT_GLYPHS.length];
      put(ox, gy + 4, glyph); put(ox + 1, gy + 4, glyph);
      const path = room.tab === 'rough-cut'
        ? `/project/${projectId}/episode/${episodeId}/rough-cut`
        : `/project/${projectId}/episode/${episodeId}/${room.tab}`;
      const action = { type: 'route', path };
      const metadata = { fullLabel: room.name, description: group.title, projectId: String(projectId) };
      mats.push({ tx: ox, ty: gy + 4, label: `进入 ${room.name}`, action, ...metadata });
      mats.push({ tx: ox + 1, ty: gy + 4, label: `进入 ${room.name}`, action, ...metadata });
      zones.push({
        x: ox, y: gy + 1, w: 2, h: 4, kind: roomKind(room.name), title: room.name,
        subtitle: group.title,
      });
      labels.push({ x: ox + 0.5, y: gy + 0.6, text: room.name, small: true, zoneLabel: true });
    });
    // 空槽位（本组不足一行）：盆栽补位
    for (let i = group.rooms.length; i < perRow; i += 1) {
      put(HALL_X0 + i * (ALCOVE_W + ALCOVE_GAP), gy + 2, 'P');
    }
  });

  // ---- 底部：休息区 / 盆栽 / 咖啡机 + 出口（返回项目楼层） ----
  put(3, exitY, 'S'); put(4, exitY, 'S');
  put(6, exitY, 'P'); put(16, exitY, 'P');
  put(21, exitY, 'M');
  put(19, exitY, 'q');
  mats.push({
    tx: 19, ty: exitY,
    label: '返回选集楼层',
    action: { type: 'route', path: `/project/${projectId}/office`, state: { projectName: projectName || '项目空间' } },
  });
  labels.push({ x: 3.5, y: exitY - 0.45, text: '休息区', small: true });
  labels.push({ x: 19, y: exitY - 0.45, text: '出口', small: true });

  const short = truncateName(projectName);
  const epShort = truncateName(episodeLabel || '本集', 12);
  const roleNames = ['阿镜 · 分镜师', '阿剪 · 剪辑师', '阿声 · 声音师', '阿资 · 资产管理员'];
  const activities = ['整理故事板', '检查节奏', '整理声音素材', '核对参考资产'];
  const roomLines = [
    ['这一排处理剧本、小故事板、导演台和主镜参考。', '先把故事讲清楚，再决定要生成多少内容。'],
    ['这一排处理画布、动态分镜和粗剪。', '粗剪适合检查整集节奏，不会改变原始分镜。'],
    ['图片、视频和音乐都从这一排进入。', '生成工具会继续沿用项目本身的设置。'],
    ['这里可以维护角色、场景、道具和改编资料。', '参考资产整理清楚，后续生成会稳定很多。'],
  ];
  return {
    id: `project-${projectId}:episode-${episodeId}`,
    grid: rows.map((r) => r.join('')),
    labels,
    mats,
    zones,
    spawn: { x: Math.floor(width / 2) + 0.5, y: 2.5 },
    crumb: `《${short}》 · ${epShort}`,
    npcs: EPISODE_ROOM_GROUPS.map((group, index) => ({
      name: roleNames[index],
      x: width - 2.6,
      y: TOP_Y + index * BLOCK_H + 2.8,
      activity: activities[index],
      wanderRadius: 0.9,
      paletteIndex: index + 1,
      lines: roomLines[index],
    })),
    coffee: { tx: 21, ty: exitY },
    ambience: 'local',
  };
}
